import { ConnectedBadge } from "@/react/components/ConnectionButtons/ConnectedBadge";
import { ConnectionButtonsOrchestrator } from "@/react/components/ConnectionButtons/ConnectionButtonsOrchestrator";
import { useConnectionButtonState } from "@/react/components/ConnectionButtons/hooks/useConnectionButtonState";
import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { useIsConnectPopoverPanel } from "@/react/components/shared/Connect/ConnectPopoverContext";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { useProfileLinkProps } from "@/react/hooks/profile/useProfileLinkProps";
import { isCommunityMember, type Person } from "@/react/types/AudienceList";

import { MemberCardImage } from "../../../CardView/MemberCardImage";
import { ExpandableBio } from "../ExpandableBio";

export const MobileCommunityMember = ({
  canReceiveDmFromCurrentMember,
  communityMember,
  isLastItem,
}: {
  canReceiveDmFromCurrentMember: boolean;
  communityMember: Person;
  isLastItem: boolean;
}) => {
  const profileLinkProps = useProfileLinkProps({
    public_uid: communityMember.public_uid ?? "",
  });

  const {
    isPendingReceived: isOtherUserSentConnection,
    isActive: isConnected,
    connection: memberConnection,
  } = useConnectionButtonState(
    isCommunityMember(communityMember) ? communityMember : undefined,
  );

  const isConnectPopoverPanel = useIsConnectPopoverPanel();

  return (
    <>
      <li
        className="bg-primary group first:mt-4 first:md:mt-0"
        data-testid="community_member"
      >
        <div className="flex w-full gap-x-4">
          <a {...profileLinkProps}>
            <div style={{ aspectRatio: "1 / 1" }} className="h-[60px]">
              <MemberCardImage
                name={communityMember.name ?? ""}
                src={communityMember.avatar_url ?? ""}
                className="!rounded-full"
              />
            </div>
          </a>

          <section className="flex min-w-0 flex-1 flex-col gap-y-3">
            <div className="flex flex-col gap-y-2">
              <div className="flex flex-col gap-y-0.5">
                <div className="flex items-center justify-between gap-y-1">
                  <Typography.LabelSm weight="semibold">
                    {communityMember.name}
                  </Typography.LabelSm>
                  {isConnected && !isConnectPopoverPanel && (
                    <ConnectedBadge
                      connectedAt={
                        memberConnection?.accepted_at ??
                        memberConnection?.created_at
                      }
                      size="sm"
                    />
                  )}
                </div>

                {communityMember.headline && (
                  <Typography.LabelXs truncate>
                    {communityMember.headline}
                  </Typography.LabelXs>
                )}
              </div>

              {communityMember.bio && (
                <ExpandableBio bio={communityMember.bio} lineClamp={2} />
              )}

              {isOtherUserSentConnection && memberConnection?.note && (
                <div className="-mb-2">
                  <ConnectionNote
                    note={memberConnection?.note}
                    lineClamp={1}
                    collapse
                    connectionId={memberConnection?.id?.toString()}
                    member={communityMember}
                  />
                </div>
              )}

              {isCommunityMember(communityMember) && !isConnectPopoverPanel && (
                <ConnectionButtonsOrchestrator
                  canReceiveDmFromCurrentMember={canReceiveDmFromCurrentMember}
                  member={communityMember}
                  showMomentumModal={false}
                  acceptButtonPosition="left"
                />
              )}
            </div>
          </section>
        </div>
      </li>
      {!isLastItem && <div className="border-primary h-4 border-b" />}
    </>
  );
};
